import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { WarehouseTaskExecutionModeValues, WarehouseTaskKindValues, WAREHOUSE_TASK_KIND_LABELS } from '../types/warehouse-task-type.js';
import { WAREHOUSE_TASK, WarehouseTaskStatusValues, WAREHOUSE_TASK_STATUS_LABELS, } from '../types/warehouse-task.js';
import { USER_DATA } from '../types/user.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for WarehouseTask (header) — read via `/pg/v1`.
 * Mutated only by `/stock/tasks*` (request 0141 — phase 2 of spec 0036).
 */
export const warehouseTaskDescriptor = {
    name: WAREHOUSE_TASK,
    label: 'Tarefa de Armazém',
    domain: 'stock',
    collectionPath: 'org/{orgId}/warehouse-task',
    pathPatternKeys: { orgId: 'string' },
    version: 2,
    description: 'Directed WMS task header — queue item with an owner',
    repositoryConfig: {
        dataSource: DataSourceStrategy.PostgresRest,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: [DataSourceStrategy.PostgresRest],
    writePolicy: 'readonly',
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'warehouseTaskTypeId',
            label: 'Tipo',
            type: 'string',
            required: false,
            description: 'Null = built-in default (org has no type cadastrado for the verb)',
        },
        {
            name: 'kind',
            example: 'check',
            label: 'Verbo',
            type: 'enum',
            required: true,
            enumValues: [...WarehouseTaskKindValues],
            enumLabels: WAREHOUSE_TASK_KIND_LABELS,
            enumTones: {
                putaway: 'success',
                pick: 'info',
                move: 'warning',
                count: 'neutral',
                check: 'destructive',
            },
        },
        {
            name: 'executionMode',
            example: 'list',
            label: 'Formato de execução',
            type: 'string',
            required: false,
            enumValues: [...WarehouseTaskExecutionModeValues],
            description: 'Snapshot from type at generation — request 0211',
        },
        {
            name: 'printVolumeLabelsOnFinish',
            example: true,
            label: 'Imprimir etiquetas ao concluir',
            type: 'boolean',
            required: false,
            description: 'Snapshot from type at generation — spec 0052 D9 / request 0324',
        },
        {
            name: 'status',
            example: 'done',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...WarehouseTaskStatusValues],
            enumLabels: WAREHOUSE_TASK_STATUS_LABELS,
            enumTones: {
                open: 'neutral',
                assigned: 'info',
                'in-progress': 'primary',
                paused: 'warning',
                done: 'success',
                cancelled: 'destructive',
            },
        },
        { name: 'priority', example: 902, label: 'Prioridade', type: 'number', required: true },
        { name: 'distributionCenterId', label: 'CD', type: 'string', required: false },
        { name: 'sourceType', label: 'Tipo origem', type: 'string', required: false },
        { name: 'sourceId', label: 'Id origem', type: 'string', required: false },
        {
            name: 'operatorNote',
            example: 'Conferência NF 12345 — 14 volumes',
            label: 'Observação ao operador',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.OPERATOR_NOTE,
        },
        {
            name: 'cancelReason',
            label: 'Motivo do cancelamento',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.OPERATOR_NOTE,
        },
        { name: 'assignedTo', label: 'Atribuído a', type: 'string', required: false },
        { name: 'startedAt', example: '2026-10-20', label: 'Início', type: 'date', display: 'datetime', required: false },
        { name: 'pausedAt', label: 'Pausada em', type: 'date', display: 'datetime', required: false },
        { name: 'completedAt', example: '2026-10-20', label: 'Conclusão', type: 'date', display: 'datetime', required: false },
        { name: 'createdBy', label: 'Criado por', type: 'string', required: false },
        { name: 'createdAt', example: '2026-10-20', label: 'Criado em', type: 'date', display: 'datetime', required: false },
    ],
    foreignKeys: [
        {
            fieldName: 'warehouseTaskTypeId',
            targetEntity: 'WarehouseTaskType',
            relationship: 'many-to-one',
            displayField: 'name',
        },
        {
            fieldName: 'distributionCenterId',
            targetEntity: 'DistributionCenter',
            relationship: 'many-to-one',
            displayField: 'name',
        },
        // `expand: false` — label-only (0167 §11): the user is resolved by the System API,
        // the row carries just the uid, and there is no join to navigate.
        {
            fieldName: 'assignedTo',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
        },
        {
            fieldName: 'createdBy',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
        },
    ],
};
//# sourceMappingURL=warehouse-task.descriptor.js.map